import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { SessionExpiredOverlay } from "../../../components/SessionExpiredOverlay"
import { Answers } from "../../../utils/Answers"
import { Question } from "../../../utils/api-objects/Question"
import { createSurveyCompleted, getQuestionsByIds, saveMultipleAnswers } from "../../../utils/apis/emr-service"
import { ANSWERS_OPENING_INPUT, ENVIRONMENT } from "../../../utils/Constants"
import { Environment } from "../../../utils/enums/Environment"
import { QuestionType, SurveyType } from "../../../utils/enums/Surveys"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useSurveyDataStore } from "../../../utils/stores/useSurveyDataStore"
import { useUserStore } from "../../../utils/stores/useUserStore"
import { useIsMetabolicUser } from "../../metabolic-health/hooks/useMetabolicHealth"
import LoadingSpinner from "../../shared/components/Loaders"
import { CloseDialog } from "../components/CloseDialog"
import { QuestionCard } from "../components/QuestionCard"
import { SurveyFooter } from "../components/SurveyFooter"
import { SurveyHeader } from "../components/SurveyHeader"
import { useQuestionNavigation } from "../hooks/useQuestionNavigation"
import { useSurveyEvents } from "../hooks/useSurveyEvents"
import { useSurveyResponses } from "../hooks/useSurveyResponses"
import { HEALTH_ASSESSMENT_IDS_DEV_METABOLIC, HEALTH_ASSESSMENT_IDS_PROD_METABOLIC } from "../utils/constants"

const HealthAssessmentSurvey = () => {
    const { sendEvent } = useCreateEvent()

    const navigate = useNavigate()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const userId = auth.userId
    const { userData } = useUserStore()
    const email = userData.email
    const { surveyData, updateSurveyData } = useSurveyDataStore()
    const ids = surveyData.healthAssessmentQuestions
    const questionsReady = ids !== null

    const [isFirstLoading, setIsFirstLoading] = useState(true)
    const [isLastLoading, setIsLastLoading] = useState(false)
    const [questions, setQuestions] = useState<Question[]>()
    const [shouldSaveResponses, setShouldSaveResponses] = useState(false);
    const [shouldShowCloseDialog, setShouldShowCloseDialog] = useState(false)
    const [isSessionExpired, setIsSessionExpired] = useState(false)

    const isMetabolicUser = useIsMetabolicUser()

    const {
        currentQuestion,
        index,
        maximumIndex,
        shouldShowCloseDialog: questionNavigationShouldShowCloseDialog,
        onBackButtonClick,
        onCloseDialogDismissClick,
        onCloseDialogConfirmClick,
        onBackClick,
        goToNextQuestion,
        progressPercentage
    } = useQuestionNavigation({
        questions,
        onQuestionChange: () => {
            if (scrollRef.current) {
                scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
            }
        }
    });

    const {
        singleValueAnswer,
        openParagraphAnswer,
        selectedRadioAnswer,
        selectedAnswers,
        customInputValue,
        surveyResponses,
        hasAnswered,
        choiceBecomesInput,
        isOldAnswerCustom,
        oldAnswerCustom,
        onSingleValueChange,
        onOpenParagraphChange,
        onMultiSelectCardClick,
        onRadioCardClick,
        handleCustomInputChange,
        saveAnswer,
        isConfirmButtonDisabled,
        setSurveyResponses: surveyResponsesSetSurveyResponses
    } = useSurveyResponses({ 
        currentQuestion, 
        goToNextQuestion,
        setQuestions 
    });

    const { trackPageView, trackAnswerSubmission, trackSurveyCompletion } = useSurveyEvents();

    const scrollRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!surveyData.healthAssessmentQuestions) return;

        getQuestionsByIds(
            surveyData.healthAssessmentQuestions,
            (questions) => {
                const idsList = ENVIRONMENT === Environment.DEVELOP
                    ? HEALTH_ASSESSMENT_IDS_DEV_METABOLIC
                    : HEALTH_ASSESSMENT_IDS_PROD_METABOLIC;

                const sortedQuestions = isMetabolicUser
                    ? questions.sort((a, b) => {
                        const indexA = idsList.indexOf(String(a.id));
                        const indexB = idsList.indexOf(String(b.id));
                        return indexA - indexB;
                    })
                    : questions.sort((a, b) => parseInt(a.id) - parseInt(b.id));

                setIsFirstLoading(false);
                setQuestions(sortedQuestions);
            },
            (error) => {
                console.log(error);
                setIsFirstLoading(false);
            }
        );
    }, [surveyData.healthAssessmentQuestions, isMetabolicUser]);

    useEffect(() => {
        if (!idToken) {
            setIsSessionExpired(true);
        }
    }, [idToken]);

    const onSubmitAnswerClick = async () => {
        if (!currentQuestion) return;

        if (currentQuestion.question_type === QuestionType.MultiSelect) {
            if (ANSWERS_OPENING_INPUT.some(answer => selectedAnswers.has(answer))) {
                const newAnswers = new Set(selectedAnswers);
                ANSWERS_OPENING_INPUT.forEach(answer => newAnswers.delete(answer));
                newAnswers.add(customInputValue);
                saveAnswer(newAnswers);
            } else {
                saveAnswer(selectedAnswers);
            }
        } else if (currentQuestion.question_type === QuestionType.Radio) {
            if (choiceBecomesInput) {
                saveAnswer(new Set([customInputValue]));
            } else {
                saveAnswer(new Set([selectedRadioAnswer!]));
            }
        } else if (currentQuestion.question_type === QuestionType.SingleValueText ||
            currentQuestion.question_type === QuestionType.SingleValueNumber) {
            saveAnswer(new Set([singleValueAnswer]));
        } else if (currentQuestion.question_type === QuestionType.OpenParagraph) {
            saveAnswer(new Set([openParagraphAnswer]));
        }

        await trackAnswerSubmission(currentQuestion);

        if (index < maximumIndex) {
            goToNextQuestion();
        } else {
            setShouldSaveResponses(true);
        }
    };

    const saveSurveyResponses = () => {
        if (!userId || !idToken) {
            return
        }
        // Convert Map to array of Answers objects
        const responsesArray: Answers[] = Array.from(surveyResponses.entries()).map(([questionId, answers]) => ({
            question_id: questionId.toString(), // Convert to string if necessary
            value: Array.from(answers),
        }));

        setIsLastLoading(true)
        saveMultipleAnswers(
            userId,
            idToken,
            responsesArray,
            () => {
                setIsLastLoading(false)
                updateSurveyData({ healthAssessmentCompleted: true })
                updateSurveyData({
                    healthAssessmentAnswers: responsesArray.map(resp => ({
                        id: resp.question_id,
                        answers: resp.value,
                    }))
                })
                createSurveyCompleted(
                    userId,
                    idToken,
                    SurveyType.HealthAssessment,
                    responsesArray,
                    () => {
                        trackSurveyCompletion(surveyResponses);
                        navigate("/health-assessment-success-page")
                    },
                    (error) => { console.log(error) }
                )
            },
            (error) => {
                console.log(error)
                setIsLastLoading(false)
            }
        )
    }

    useEffect(() => {
        if (shouldSaveResponses) {
            saveSurveyResponses();
        }
    }, [shouldSaveResponses]);

    useEffect(() => {
        if (questionNavigationShouldShowCloseDialog) {
            setShouldShowCloseDialog(true);
        }
    }, [questionNavigationShouldShowCloseDialog]);

    useEffect(() => {
        if (currentQuestion) {
            trackPageView();
        }
    }, [currentQuestion]);

    if (isFirstLoading) {
        return <LoadingSpinner />;
    }

    if (!questions || !currentQuestion) {
        return null;
    }

    return (
        <div className="w-full h-screen flex flex-col fixed inset-0">
            <SessionExpiredOverlay isVisible={isSessionExpired} />
            {isLastLoading && (
                <div className="fixed inset-0 bg-white/80 z-50 flex items-center justify-center">
                    <LoadingSpinner />
                </div>
            )}
            <div className="flex-none sticky top-0 z-10">
                <SurveyHeader
                    currentQuestion={currentQuestion}
                    progressPercentage={progressPercentage}
                    onClose={() => setShouldShowCloseDialog(true)}
                />
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto">
                <div className="w-full flex flex-col items-center py-[20px] px-[20px] md:px-[40px]">
                    <QuestionCard
                        question={currentQuestion}
                        singleValueAnswer={singleValueAnswer}
                        openParagraphAnswer={openParagraphAnswer}
                        selectedRadioAnswer={selectedRadioAnswer}
                        selectedAnswers={selectedAnswers}
                        customInputValue={customInputValue}
                        choiceBecomesInput={choiceBecomesInput}
                        isOldAnswerCustom={isOldAnswerCustom}
                        oldAnswerCustom={oldAnswerCustom}
                        onSingleValueChange={onSingleValueChange}
                        onOpenParagraphChange={onOpenParagraphChange}
                        onMultiSelectCardClick={onMultiSelectCardClick}
                        onRadioCardClick={onRadioCardClick}
                        onCustomInputChange={handleCustomInputChange}
                    />
                </div>
            </div>
            <div className="flex-none sticky bottom-0 z-10 bg-white">
                <SurveyFooter
                    isLastQuestion={index === maximumIndex}
                    isSubmitDisabled={isConfirmButtonDisabled()}
                    onSubmit={onSubmitAnswerClick}
                    onBack={onBackClick}
                />
            </div>
            {shouldShowCloseDialog && (
                <CloseDialog
                    onDismissClick={() => {
                        setShouldShowCloseDialog(false);
                        navigate("/health-assessment-home-page");
                    }}
                    onConfirmClick={() => setShouldShowCloseDialog(false)}
                />
            )}
        </div>
    );
};

export default HealthAssessmentSurvey;
